import React, { useCallback, useContext, useEffect, useState } from "react";
import _ from "lodash";
import {
  Badge,
  Button,
  LegacyCard as Card,
  Text,
  DataTable,
  FormLayout,
  Link,
  Modal,
  InlineStack,
  TextField,
  Thumbnail,
  Box,
  Divider,
  InlineCode,
} from "@shopify/polaris";

import { Spinner } from "lib/components";

import { baseHelper, errorHelper } from "lib/helpers";
import { useMutation, useQuery } from "@apollo/client";

import constant from "lib/constant/constant";
import { PrivateContext } from "lib/context";

import { UPDATE_TRACKING_DETAIL } from "app/orders/apollo/mutations/index";

// import queries
import { GET_MANAGE_FULFILLMENT_SETTING } from "app/setup/apollo/queries";

import { fulfillmentsProp } from "app/orders/modules/operator/features/view/propsType";
import { FieldSetQty } from "app/orders/modules/operator/features/view/modal/modalStyle";
import { useNavigate } from "react-router-dom";

const Fulfillments = (props) => {
  const { gql } = constant;
  const { cms } = useContext(PrivateContext);
  const navigate = useNavigate();
  const [editModal, setEditModal] = useState(false);
  const [trackingCompany, setTrackingCompany] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [quantity, setQuantity] = useState();
  const [totalLineItemQuantity, setTotalLineItemQuantity] = useState();
  const [fulfillmentId, setFulfillmentId] = useState("");
  const [viewLineOrderData, setViewLineOrderData] = useState({});
  const [isEnableFulfillmentSetting, setIsEnableFulfillmentSetting] = useState(false);
  const [updateTrackingDetail, setUpdateTrackingDetail] = useState(true);

  const {
    lineData,
    fulfillmentsCount,
    fulfillmentType,
    lineCount,
    setFulfillmentType,
    setBanner,
    setMessage,
    refetchOrderFulfillment,
  } = props;
  const { fulfillmentType: type, _id: lineItemId } = viewLineOrderData;
  const [updateTracking, { loading: loadingTracking, error: errorTracking, data: trackingData }] = useMutation(
    UPDATE_TRACKING_DETAIL
  );

  const { data: fulfillSettingData, loading: loadingFulfillment } = useQuery(GET_MANAGE_FULFILLMENT_SETTING);

  const row = [];
  let totalQuantity;
  let fulfilledQuantity = 0;
  lineCount.forEach((item) => {
    if (item.fulfillableQuantity === 0) {
      fulfilledQuantity = fulfillmentsCount;
    }
  });

  const handleChange = useCallback(() => setEditModal(!editModal), [editModal]);

  const validInput = (value) => {
    return baseHelper.stringNotAcceptSpaceAtStart(value) && value;
  };

  const editTrackingDetails = () => {
    const formValues = {
      shopifyFulfillmentId: fulfillmentId,
      trackingCompany,
      trackingNumber,
      lineItemId,
      trackingUrl: "",
    };
    updateTracking({ variables: { input: formValues } });
    setUpdateTrackingDetail(true);
  };

  useEffect(() => {
    if (fulfillSettingData) {
      const resData = baseHelper.getResponseData(fulfillSettingData, gql.GET_MANAGE_FULFILLMENT_SETTING);
      const resError = baseHelper.getResponseError(fulfillSettingData, gql.GET_MANAGE_FULFILLMENT_SETTING);
      if (resError) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: resError,
        });
        return;
      }
      if (resData) {
        const { edit } = resData || {};
        setIsEnableFulfillmentSetting(edit);
      }
    }
  }, [fulfillSettingData, setBanner, gql.GET_MANAGE_FULFILLMENT_SETTING]);

  useEffect(() => {
    if (trackingData && updateTracking) {
      const responseTracking = baseHelper.getResponseData(trackingData, constant.gql.UPDATE_TRACKING_DETAIL);
      const responseTrackingError = baseHelper.getResponseError(trackingData, constant.gql.UPDATE_TRACKING_DETAIL);
      if (responseTracking) {
        setEditModal(false);
        setMessage(cms("message.success.updateTracking"));
        refetchOrderFulfillment();
      }
      if (responseTrackingError) {
        setEditModal(false);
        setBanner({
          status: constant.CRITICAL,
          title: errorHelper.parse(responseTrackingError),
          isOpen: true,
        });
      }
      setUpdateTrackingDetail(false);
    }
  }, [setBanner, setMessage, trackingData, updateTrackingDetail, updateTracking, cms, refetchOrderFulfillment]);

  useEffect(() => {
    setEditModal(false);
    if (errorTracking) {
      setBanner({
        status: constant.CRITICAL,
        title: errorHelper.parse(errorTracking),
        isOpen: true,
      });
    }
  }, [errorTracking, setBanner]);

  const badgeStatus =
    baseHelper.getBadgeStatus(type) === constant.PICKUP
      ? constant.CONSIGNMENT_PICKUP_CAPITAL
      : baseHelper.getBadgeStatus(type);

  const openModel = (selectedId, value, fulfillableQuantity) => {
    setTrackingCompany(value.trackingCompany);
    setTrackingNumber(value.trackingNumber);
    setQuantity(fulfillableQuantity);
    setTotalLineItemQuantity(value.lineItems[0].totalQuantity);
    setFulfillmentId(value.shopifyFulfillmentId);
    setViewLineOrderData(lineCount.find((item) => item.shopifyLineItemId === selectedId));
    setEditModal(true);
  };

  const editTrackingModal = () => {
    return (
      <>
        <Modal
          open={editModal}
          onClose={() => {
            handleChange(false);
          }}
          title={cms("label.editTracking")}
          primaryAction={{
            content: cms("label.updateDetails"),
            disabled: !(trackingCompany && trackingNumber),
            onAction: () => {
              editTrackingDetails();
            },
            loading: loadingTracking,
          }}
          sectioned
        >
          <Modal.Section>
            <FormLayout>
              <InlineStack wrap={false} id={viewLineOrderData._id} gap="400">
                <Thumbnail
                  size="large"
                  source={viewLineOrderData.image ? viewLineOrderData.image : constant.NOIMAGESNAP}
                  alt={viewLineOrderData.name}
                />
                <Box width="100%">
                  <InlineStack align="space-between">
                    <div className="ellipsis">
                      {(viewLineOrderData.isProductDeleted && (viewLineOrderData.name || "")) || (
                        <Link onClick={() => navigate(`/products/edit/${viewLineOrderData.productId}`)}>
                          {viewLineOrderData.name || ""}
                        </Link>
                      )}
                      {viewLineOrderData.variantTitle && (
                        <Text variant="bodySm" as="p">
                          <Text as="span" color={constant.SUBDUED}>
                            {viewLineOrderData.variantTitle.toUpperCase()}
                          </Text>
                        </Text>
                      )}
                      <Badge tone={badgeStatus} className="text-capitalize">
                        {badgeStatus === constant.CONSIGNMENT ? constant.CONSIGNMENT_SHIP_CAPITAL : badgeStatus}
                      </Badge>
                    </div>
                    <FieldSetQty>
                      <TextField
                        value={quantity}
                        disabled
                        suffix={`${cms("label.of")} ${
                          viewLineOrderData.actualQuantity - viewLineOrderData.fulfillableQuantity
                        }`}
                      />
                    </FieldSetQty>
                  </InlineStack>
                </Box>
              </InlineStack>
              <TextField
                label={cms("label.company")}
                value={trackingCompany}
                onChange={(val) => {
                  setTrackingCompany(validInput(val));
                }}
              />
              <TextField
                label={cms("label.trackingNumber")}
                value={trackingNumber}
                onChange={(val) => {
                  setTrackingNumber(validInput(val));
                }}
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
      </>
    );
  };

  let typeData = [];
  if (lineData) {
    lineData.forEach((value) => {
      const { lineItems } = value;
      lineItems.forEach((lineItem) => {
        const found = lineCount.find(
          (item) =>
            item.shopifyLineItemId === lineItem.id &&
            [constant.RESERVE, constant.COLLECT, constant.SERVICE].includes(item.fulfillmentType)
        );
        if (!found) {
          const data = lineCount.find((item) => item.shopifyLineItemId === lineItem.id && item);
          let isShowEnableFulfillmentButton = true;
          if (data.isDelivered) {
            isShowEnableFulfillmentButton = false;
          }
          if (data.isReturn && !(data.quantity > data.returnedQuantity)) {
            isShowEnableFulfillmentButton = false;
          }
          typeData = lineItem.fulfillmentType;
          const { shipsyDetails, consignmentUrl } = data;
          row.push([
            <div className="ellipsis">
              <Link>{lineItem.name}</Link>
              <Text variant="bodySm" as="p">
                <br />
                <Text as="span">
                  {`${cms("label.vendor")}: `}
                  <Link>{lineItem.vendor}</Link>
                </Text>
              </Text>
              <br />
              {shipsyDetails && shipsyDetails.type ? (
                <Text as="span">
                  Last Event:
                  <InlineCode>{baseHelper.convertToPascalCase(shipsyDetails.type)}</InlineCode>
                </Text>
              ) : (
                ""
              )}
              {shipsyDetails && shipsyDetails.event_time ? (
                <Text as="span">
                  Event Time:
                  <Text as="span">{baseHelper.formatDate(new Date(shipsyDetails.event_time))}</Text>
                </Text>
              ) : (
                ""
              )}
              {consignmentUrl ? (
                <Link target="_blank" url={consignmentUrl}>
                  Airway Bill
                </Link>
              ) : (
                ""
              )}
            </div>,

            <Text as="span">{value.trackingCompany}</Text>,
            <>
              {value.trackingNumber}
              <br />
              <br />
              {isEnableFulfillmentSetting && isShowEnableFulfillmentButton && (
                <Button onClick={() => openModel(lineItem.id, value, lineItem.quantity)}>
                  {cms("button.editFulfillment")}
                </Button>
              )}
            </>,
          ]);
        }
      });
    });
    if (!_.isEqual(fulfillmentType, typeData)) {
      setFulfillmentType(typeData);
    }
    totalQuantity = (lineCount && lineCount.length) || 0;
  }
  if (loadingFulfillment) {
    return <Spinner />;
  }

  return (
    <>
      {editTrackingModal()}
      <Box padding={"400"}>
        <DataTable
          columnContentTypes={["text", "text", "numeric"]}
          headings={[cms("label.product"), cms("label.shippingCompany"), cms("label.trackingNumber")]}
          rows={row}
        />
      </Box>
      <Divider />
      <Box padding={"400"}>
        <InlineStack align="space-between">
          <Text as="span" fontWeight="semibold">
            {cms("label.totalFulfillments")}
          </Text>
          <Text as="span" fontWeight="semibold">{`${fulfilledQuantity} ${cms("label.of")} ${totalQuantity}`}</Text>
        </InlineStack>
      </Box>
    </>
  );
};
Fulfillments.propTypes = fulfillmentsProp.type;
export default Fulfillments;
